'use client'

import React, {useState, useEffect} from 'react';
import {Button, Checkbox} from "@/components/UiElements/index";


// SearchBar Interfaces
export interface SearchBarOptions {
    label: React.ReactNode;
    icon?: React.ReactElement | string;
    iconClassName?: string;
    iconPosition?: 'left' | 'right';
    className?: string;
    onClick?: () => void;
    type? : 'primary' | 'alt' | 'danger';
    disabled?: boolean;
}
export interface SearchBarFilters {
    label: string;
    default?: boolean;
    onClick: (value:boolean) => void;
    count?: number;
}
export interface SearchBarProps {
    placeholder?: string;
    onSearch?: (searchText: string) => void;
    onChange?: (searchText: string) => void;
    filters?: Array<SearchBarFilters>;
    options?: Array<SearchBarOptions>;
    autoFocus?: boolean;
    searchText?: string;
}



// SearchBar Component
const SearchBar: React.FC<SearchBarProps> = ({
    placeholder = "Search...",
    onSearch,
    onChange,
    filters = [],
    options = [],
    autoFocus = false,
    searchText = ""

}) => {

    const [focused, setFocused] = useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)


    // Handle search on enter
    const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && onSearch) {
            onSearch(e.currentTarget.value);
        }
    };

    // Handle change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if(onChange) {
            onChange(e.target.value)
        }
    }

    // Clicking the chrome around the input (padding, filter chips) must not blur
    // the field, hence the preventDefault. The input itself is excluded: killing
    // its default mousedown also kills drag-select, double-click-select and
    // caret placement inside the field.
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === inputRef.current) return;

        e.preventDefault();
    };

    // Mirrors programmatic changes (Clear button, filter resets) into the
    // uncontrolled input. Skipped when the DOM already holds the value, because
    // assigning to .value moves the caret to the end — which would make editing
    // in the middle of the text impossible while typing.
    useEffect(() => {
        if (inputRef.current && inputRef.current.value !== searchText) {
            inputRef.current.value = searchText;
        }
    }, [searchText]);




    return (
        <div className="flex gap-sm">
            <div
                className={
                    "flex-grow h-[50px] border border-gray-300 rounded-md flex-row flex gap-md items-center px-md pr-[6px] transition-all cursor-text hover:border-primary-500 " +
                    `${focused ? ' shadow-focus border-primary-500 ' : ' shadow-xs '}`
                }
                onMouseDown={handleMouseDown}
                onClick={() => inputRef.current?.focus()}
            >
                <span className={'font-icon-regular text-title-lg text-gray-400'}></span>



                <input
                    type="text"
                    className="text-gray-900 flex flex-grow bg-transparent outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 text-title-md placeholder-gray-300 placeholder:text-text-md font-normal"
                    placeholder={placeholder}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onChange={handleChange}
                    onKeyDown={handleSearch}
                    ref={inputRef}
                    autoFocus={autoFocus}
                />


                {filters.length > 0 && (
                    <div className={'flex flex-row gap-xs'}>
                        {filters.map((filter, index) => (
                            <Checkbox
                                key={index}
                                label={filter.label}
                                description={typeof filter.count === "number" ? `(${filter.count})` : null}
                                className={(select) => (select ? 'bg-primary-50 h-[38px] px-md rounded-md ' : 'px-md h-[38px]')}
                                onChange={filter.onClick}
                                defaultChecked={filter.default}
                            />
                        ))}
                    </div>
                )}
            </div>
            {options.length > 0 && (
                <div className="flex items-center gap-sm">
                    {options.map((option, index) => (
                        <Button
                            key={index}
                            size={'md'}
                            type={option.type || 'alt'}
                            className={`h-[50px] ${option.className || ""}`}
                            onClick={option.onClick}
                            disabled={option.disabled || false}
                            icon={option.icon}
                            iconClassName={option.iconClassName}
                            iconPosition={option.iconPosition}
                        >
                            <>
                                {
                                    typeof option.label === 'string' || React.isValidElement(option.label)?
                                        option.label
                                    :
                                    null
                                }
                            </>
                        </Button>
                    ))}
                </div>
            )}





        </div>
    );
}

SearchBar.displayName = "SearchBar";

export default SearchBar;
